import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faUsers, faDatabase, faRobot, faServer, faCheck, faTriangleExclamation, faPlug, faXmark,
  faArrowRightArrowLeft, faWaveSquare, faFlaskVial,
} from '@fortawesome/free-solid-svg-icons';
import { setUser } from './auth';
import { ConsoleContext } from './admin/context';
import { createClient, ownTarget, remoteSession, connectRemote, remoteTarget, forgetRemoteSession } from './admin/client';
import { deepClone, EMPTY_USER, Field, PasswordInput, Spinner } from './admin/ui';
import { Editor, UserList } from './admin/UsersSection';
import DataSourcesSection from './admin/DataSourcesSection';
import AssistantsSection from './admin/AssistantsSection';
import InstancesSection from './admin/InstancesSection';
import ActivityCenter from './activity/ActivityCenter';
import TestSuite from './testsuite/TestSuite';
import './UserManagement.css';
import './admin/console.css';

// Admin console. Users (admins), plus data sources, assistant bindings and
// instances (super admins). It manages THIS instance — or, for super admins,
// another registered instance they connect to with an account there; the
// browser then talks to that instance directly (admin/client.js).
// Who may do what is decided by the server: src/controller/user_admin.py and
// src/controller/admin_console.py.

const SECTIONS = [
  { key: 'users', label: 'Users', icon: faUsers },
  { key: 'sources', label: 'Data sets', icon: faDatabase, superadmin: true },
  { key: 'assistants', label: 'Assistants', icon: faRobot, superadmin: true },
  { key: 'instances', label: 'Instances', icon: faServer, superadmin: true },
];

const ENV_LABEL = { dev: 'Dev', test: 'Test', prod: 'Prod' };

function ConnectDialog({ instance, onConnected, onCancel }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try { onConnected(await connectRemote(instance, username, password)); } catch (err) { setError(err.message); }
    setBusy(false);
  };
  return (
    <div className="um-overlay" onClick={onCancel}>
      <form className="um-dialog wide" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3><FontAwesomeIcon icon={faPlug} /> Connect to {instance.label}</h3>
        <p>Log in with your administrator account <strong>on that instance</strong> ({instance.url}). The session is kept for this browser tab only.</p>
        <div className="um-dialog-fields">
          <Field label="Username"><input value={username} autoFocus autoComplete="username" onChange={(e) => setUsername(e.target.value)} /></Field>
          <Field label="Password"><PasswordInput value={password} onChange={setPassword} autoComplete="current-password" /></Field>
        </div>
        {error && <div className="um-hint error">{error}</div>}
        <div className="um-dialog-btns">
          <button type="button" className="um-btn um-btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="um-btn um-btn-primary" disabled={busy || !username || !password}>{busy ? 'Connecting…' : 'Connect'}</button>
        </div>
      </form>
    </div>
  );
}

function UserManagement({ onBack, onUserUpdated }) {
  const [target, setTarget] = useState(ownTarget);
  const client = useMemo(() => createClient(target), [target]);
  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [ownInstances, setOwnInstances] = useState([]);   // THIS instance's registry — the switcher's choices
  const [users, setUsers] = useState([]);
  const [botFunctions, setBotFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(null);           // { user, isNew, version }
  const [section, setSection] = useState('users');
  const [activityOpen, setActivityOpen] = useState(false);
  const [testsOpen, setTestsOpen] = useState(false);
  const [connectTo, setConnectTo] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const me = target.me;

  const flash = useCallback((type, text) => {
    setToast({ type, text });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [m, list] = await Promise.all([client.get('/api/users/meta'), client.get('/api/users')]);
      setMeta(m);
      setUsers(list);
      if (!target.remote) setOwnInstances(m.instances || []);
      if (m.permissions.manage_users) {
        client.get('/api/bot_functions').then(setBotFunctions).catch(() => setBotFunctions([]));
        client.get('/api/admin/overview').then(setOverview).catch(() => setOverview(null));
      }
    } catch (e) {
      setLoadError(e.message);
      if (target.remote && e.status === 401) setTarget(ownTarget());
    } finally {
      setLoading(false);
    }
  }, [client, target.remote]);

  useEffect(() => { setEditing(null); load(); }, [load]);

  const switchTo = (key) => {
    if (!key) { setTarget(ownTarget()); setSection('users'); return; }
    const instance = ownInstances.find((i) => i.key === key);
    if (!instance) return;
    const session = remoteSession(key);
    if (session) { setTarget(remoteTarget(instance, session)); setSection('users'); } else setConnectTo(instance);
  };

  const disconnect = () => { forgetRemoteSession(target.key); setTarget(ownTarget()); setSection('users'); };

  const handleSaved = (saved, isNew) => {
    setUsers((prev) => (isNew ? [...prev, saved] : prev.map((u) => (u.id === saved.id ? saved : u))));
    if (!target.remote && saved.id === me.id) {
      setUser(saved);
      onUserUpdated?.();
    }
    // A new version key remounts the editor: the saved record (as the server
    // normalised it) becomes the baseline, so nothing shows as unsaved.
    setEditing({ user: saved, isNew: false, version: Date.now() });
  };

  const handleDeleted = (id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    setEditing(null);
  };

  const toastEl = toast && (
    <div className={`um-toast um-toast-${toast.type}`} role="status">
      <FontAwesomeIcon icon={toast.type === 'error' ? faTriangleExclamation : faCheck} /> {toast.text}
    </div>
  );

  if (loading && !meta) return <div className="um-page"><div className="um-loading"><Spinner /></div></div>;
  if (loadError && !meta) {
    return (
      <div className="um-page">
        <div className="um-topbar"><div className="um-topbar-left">
          <button type="button" className="um-back-btn" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /></button>
          <h1 className="um-title">User management</h1>
        </div></div>
        <div className="um-empty">{loadError} <button type="button" className="um-link-btn" onClick={load}>Retry</button></div>
      </div>
    );
  }

  const ctx = { client, meta, me };

  // A user without admin rights only ever sees their own record.
  if (!meta.permissions.manage_users) {
    const self = editing?.user || users[0];
    if (!self) return <div className="um-page"><div className="um-empty">Profile not available.</div></div>;
    return (
      <ConsoleContext.Provider value={ctx}>
        <Editor key={`self-${self.id}-${editing?.version || 0}`} original={self} isNew={false}
          botFunctions={[]} onClose={onBack} onSaved={handleSaved} onDeleted={() => {}} flash={flash} standalone />
        {toastEl}
      </ConsoleContext.Provider>
    );
  }

  if (editing) {
    return (
      <ConsoleContext.Provider value={ctx}>
        <Editor
          key={editing.isNew ? 'new' : `u-${editing.user.id}-${editing.version || 0}`}
          original={editing.user} isNew={editing.isNew} botFunctions={botFunctions}
          onClose={() => setEditing(null)} onSaved={handleSaved} onDeleted={handleDeleted} flash={flash}
        />
        {toastEl}
      </ConsoleContext.Provider>
    );
  }

  const sections = SECTIONS.filter((s) => !s.superadmin || meta.permissions.manage_instance);
  const activeSection = sections.some((s) => s.key === section) ? section : 'users';
  const inst = meta.instance || {};
  const title = target.remote ? target.label : (inst.instance_label || 'This instance');
  const env = target.remote ? target.environment : inst.instance_environment;

  return (
    <ConsoleContext.Provider value={ctx}>
      <div className="um-page">
        <div className="um-topbar">
          <div className="um-topbar-left">
            <button type="button" className="um-back-btn" onClick={onBack} title="Back to dashboard"><FontAwesomeIcon icon={faArrowLeft} /></button>
            <div>
              <h1 className="um-title">{meta.permissions.manage_instance ? 'Admin console' : 'User management'}</h1>
              <div className="um-subtitle">
                <FontAwesomeIcon icon={faServer} /> {title}
                {env && <span className={`um-env um-env-${env}`}>{ENV_LABEL[env] || env}</span>}
                {inst.instance_client && !target.remote && <span className="um-muted">· {inst.instance_client}</span>}
              </div>
            </div>
          </div>
          {(ownInstances.length > 0 || meta.permissions.manage_instance) && (
            <div className="um-topbar-right">
              {meta.permissions.manage_instance && (
                <button type="button" className="um-activity-btn" onClick={() => setActivityOpen(true)}
                  title="Who did what, when — chats, audio, analyses, documents">
                  <FontAwesomeIcon icon={faWaveSquare} /> Activity
                </button>
              )}
              {meta.permissions.manage_instance && (
                <button type="button" className="um-tests-btn" onClick={() => setTestsOpen(true)}
                  title="Questions through the assistants, checked against the client's dashboard">
                  <FontAwesomeIcon icon={faFlaskVial} /> Test Suite
                </button>
              )}
              {ownInstances.length > 0 && (
              <label className="um-switcher" title="Manage another instance">
                <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                <select value={target.key} onChange={(e) => switchTo(e.target.value)}>
                  <option value="">{(inst.instance_label && !target.remote) ? inst.instance_label : 'This instance'}</option>
                  {ownInstances.map((i) => (
                    <option key={i.key} value={i.key}>{i.label}{i.environment ? ` (${ENV_LABEL[i.environment] || i.environment})` : ''}</option>
                  ))}
                </select>
              </label>
              )}
            </div>
          )}
        </div>

        {target.remote && (
          <div className="um-remote-banner">
            <FontAwesomeIcon icon={faPlug} />
            <span>Managing <strong>{target.label}</strong> ({target.base}) as <strong>{me.username}</strong> — changes apply there.</span>
            <button type="button" className="um-link-btn" onClick={disconnect}><FontAwesomeIcon icon={faXmark} /> Disconnect</button>
          </div>
        )}

        {sections.length > 1 && (
          <nav className="um-console-nav">
            {sections.map((s) => (
              <button key={s.key} type="button" className={activeSection === s.key ? 'active' : ''} onClick={() => setSection(s.key)}>
                <FontAwesomeIcon icon={s.icon} /> {s.label}
              </button>
            ))}
          </nav>
        )}

        {activeSection === 'users' && (
          <UserList users={users} loading={loading} reload={load}
            onOpen={(u) => setEditing({ user: u, isNew: false })}
            onNew={() => setEditing({ user: deepClone(EMPTY_USER), isNew: true })} />
        )}
        {activeSection === 'sources' && <DataSourcesSection flash={flash} overview={overview} />}
        {activeSection === 'assistants' && <AssistantsSection flash={flash} />}
        {activeSection === 'instances' && (
          <InstancesSection flash={flash} overview={overview}
            onSettingsSaved={() => { load(); }} />
        )}
        {activityOpen && <ActivityCenter onClose={() => setActivityOpen(false)} />}
        {testsOpen && <TestSuite onClose={() => setTestsOpen(false)} />}
      </div>

      {connectTo && (
        <ConnectDialog instance={connectTo} onCancel={() => setConnectTo(null)}
          onConnected={(session) => { setTarget(remoteTarget(connectTo, session)); setConnectTo(null); setSection('users'); }} />
      )}
      {toastEl}
    </ConsoleContext.Provider>
  );
}

export default UserManagement;
